import type { AuctionDetail, BidHistoryEntry } from '../shared/auction-types.js';
import type { CardDef } from '../types.js';
import { apiClient } from '../api-client.js';
import { createCardElement } from './CardView.js';
import { formatCountdown } from './Countdown.js';
import { showNotice } from './NoticeView.js';
import { mergeMissingCardsByIds } from '../collection/PlayerState.js';

// Detail view: big card display (same scaling as the deck-editor's
// card-detail modal) on the left, variant strip below it, auction
// info + bid form + activity log on the right.
//
// Variant clicks swap the art in the main card preview (both for
// browsing bidders and for the winner-claim flow). The selected
// variant persists in state and is what the claim button posts.
//
// Layout is single-column in portrait so the card gets full width.
// Landscape splits card ~60% / info ~40% — the card is the thing
// bidders are evaluating.

const BID_INCREMENT = 10;
const VARIANT_COUNT = 4;

interface ViewState {
  detail: AuctionDetail;
  card: CardDef | null;
  selectedVariant: number;
}

function shardDir(id: string): string {
  return `${id.slice(0, 2)}/${id.slice(2, 4)}`;
}

function variantUrl(cardId: string, variant: number): string {
  return `data/custom-cards/${shardDir(cardId)}/${cardId}-${String(variant).padStart(2, '0')}.png`;
}

export async function createAuctionDetailView(
  container: HTMLElement,
  auctionId: string,
  catalog: Map<string, CardDef>,
  gems: number,
  onBack: () => void,
  onGemsChanged: (gems: number) => void,
  onClaimed: (cardId: string, art: string) => void,
): Promise<() => void> {
  container.innerHTML = '';
  const shell = document.createElement('div');
  shell.className = 'menu-screen auction-detail is-scrollable';
  shell.innerHTML = '<div class="auction-loading meta">Loading auction…</div>';
  container.appendChild(shell);

  let detail: AuctionDetail;
  try {
    detail = await apiClient.getAuctionDetail(auctionId);
  } catch (err) {
    console.error('Failed to load auction detail', err);
    shell.innerHTML = `
      <button class="menu-btn campaign-back" id="back-btn">&larr; Back</button>
      <div class="auction-empty meta">Couldn't load this auction.</div>
    `;
    shell.querySelector('#back-btn')!.addEventListener('click', onBack);
    return () => {};
  }

  await mergeMissingCardsByIds(catalog, [detail.cardId]);
  const card = catalog.get(detail.cardId) ?? null;

  const state: ViewState = {
    detail,
    card,
    // Prefer the authoritative variant from the server (which reads
    // custom_cards.selected_image). Fall back to guessing from the
    // art URL suffix, then to 1. The URL-guess path is a hedge for
    // future changes where the JSON stores <id>-NN.png; today the
    // server row is the source of truth.
    selectedVariant: detail.selectedVariant
      ?? guessCurrentVariantFromArt(detail.cardId, card?.art)
      ?? 1,
  };

  const refresh = async (): Promise<void> => {
    try {
      const fresh = await apiClient.getAuctionDetail(auctionId);
      state.detail = fresh;
      // The gem balance may have shifted between the initial mount
      // and the refresh (the player just placed a bid). Read the
      // current DOM value rather than reusing the stale arg.
      const current = readDomGems() ?? gems;
      render(shell, state, current, onBack, onGemsChanged, onClaimed, auctionId, refresh);
    } catch (err) {
      console.error('Failed to refresh auction', err);
    }
  };

  render(shell, state, gems, onBack, onGemsChanged, onClaimed, auctionId, refresh);

  const timer = setInterval(() => {
    const cd = shell.querySelector('.auction-countdown') as HTMLElement | null;
    if (cd) {
      const ends = parseInt(cd.dataset.endsAt ?? '0', 10);
      cd.textContent = formatCountdown(ends);
    }
  }, 1000);

  return (): void => { clearInterval(timer); };
}

function guessCurrentVariantFromArt(cardId: string, art?: string): number | null {
  if (!art) return null;
  const m = art.match(/-(\d{2})\.png$/);
  if (m) return parseInt(m[1], 10);
  return null;  // .png (no suffix) = the canonical picked variant; we don't know which
}

/** Swap only the <img src> inside the visible card so we don't tear
 *  down the whole card DOM on every variant hover. Falls back to
 *  re-rendering if the img is missing (e.g. placeholder-only card). */
function updateCardArt(shell: HTMLElement, cardId: string, variant: number): void {
  const img = shell.querySelector('.auction-detail-card-host .card-art img') as HTMLImageElement | null;
  if (img) {
    img.src = variantUrl(cardId, variant);
  }
}

function render(
  shell: HTMLElement,
  state: ViewState,
  gems: number,
  onBack: () => void,
  onGemsChanged: (gems: number) => void,
  onClaimed: (cardId: string, art: string) => void,
  auctionId: string,
  refresh: () => Promise<void>,
): void {
  const { detail, card } = state;
  shell.innerHTML = '';

  // Sticky header mirroring the browse view + tournament pattern:
  // Back on the left, card name centered, gems balance on the right.
  const headerBar = document.createElement('div');
  headerBar.className = 'auction-sticky-header';
  const back = document.createElement('button');
  back.className = 'menu-btn-sm auction-sticky-back';
  back.textContent = '← Back';
  back.addEventListener('click', onBack);
  headerBar.appendChild(back);
  const title = document.createElement('h1');
  title.className = 'auction-sticky-title';
  title.textContent = card?.name ?? detail.cardId;
  headerBar.appendChild(title);
  const actions = document.createElement('div');
  actions.className = 'auction-sticky-actions';
  const gemsEl = document.createElement('span');
  gemsEl.className = 'player-gems';
  gemsEl.textContent = `${gems} gems`;
  actions.appendChild(gemsEl);
  headerBar.appendChild(actions);
  shell.appendChild(headerBar);

  const layout = document.createElement('div');
  layout.className = 'auction-detail-layout';

  // ── Left column: big card + art variants ──────────────────────
  const left = document.createElement('div');
  left.className = 'auction-detail-card-col';
  const cardHost = document.createElement('div');
  cardHost.className = 'auction-detail-card-host';
  if (card) {
    // Clone the def with the selected variant's art URL so the initial
    // render matches what the variant picker shows. Downstream variant
    // clicks patch the <img src> in place (no re-render) via
    // updateCardArt().
    const withArt: CardDef = { ...card, art: variantUrl(detail.cardId, state.selectedVariant) };
    cardHost.appendChild(createCardElement(withArt, undefined, false, undefined, false, 0, false, true));
  } else {
    cardHost.innerHTML = '<div class="auction-empty meta">Card art unavailable.</div>';
  }
  left.appendChild(cardHost);

  const variantsSection = document.createElement('div');
  variantsSection.className = 'auction-variants';
  const isClaimable = detail.status === 'closed_sold' && detail.perspective.isWinner;
  variantsSection.innerHTML = `<div class="auction-variants-heading">${
    isClaimable ? 'Pick your art variant' : 'Art variants (click to preview)'
  }</div>`;
  const grid = document.createElement('div');
  grid.className = 'auction-variants-grid';
  for (let v = 1; v <= VARIANT_COUNT; v++) {
    const tile = document.createElement('div');
    tile.className = `auction-variant clickable${state.selectedVariant === v ? ' selected' : ''}`;
    tile.style.backgroundImage = `url('${variantUrl(detail.cardId, v)}')`;
    tile.dataset.variant = String(v);
    tile.addEventListener('click', () => {
      state.selectedVariant = v;
      updateCardArt(shell, detail.cardId, v);
      variantsSection.querySelectorAll('.auction-variant').forEach(t => t.classList.remove('selected'));
      tile.classList.add('selected');
      if (isClaimable) {
        const btn = shell.querySelector('.auction-claim-btn');
        if (btn) btn.textContent = `Claim with variant ${v}`;
      }
    });
    grid.appendChild(tile);
  }
  variantsSection.appendChild(grid);

  if (isClaimable) {
    const claimBtn = document.createElement('button');
    claimBtn.className = 'menu-btn primary-blue auction-claim-btn';
    claimBtn.textContent = `Claim with variant ${state.selectedVariant}`;
    claimBtn.addEventListener('click', () => {
      void doClaim(auctionId, detail.cardId, state.selectedVariant, onBack, onClaimed, onGemsChanged);
    });
    variantsSection.appendChild(claimBtn);
  }
  left.appendChild(variantsSection);
  layout.appendChild(left);

  // ── Right column: auction info + bid form + history ───────────
  const right = document.createElement('div');
  right.className = 'auction-detail-info-col';

  const statusLine = statusLineHtml(detail);
  const sellerLine = detail.sellerDisplayName
    ? `Sold by ${escapeHtml(detail.sellerDisplayName)}`
    : 'System listing';
  const leaderLine = detail.leaderDisplayName
    ? `Leader: <strong>${escapeHtml(detail.leaderDisplayName)}</strong>`
    : 'No bids yet.';
  const endsMs = new Date(detail.endsAt).getTime();

  right.innerHTML = `
    <div class="meta">${sellerLine}</div>
    <div class="auction-status-line">${statusLine}</div>
    <div class="auction-price-block">
      <div class="auction-price">${detail.visiblePrice} 💎</div>
      <div class="auction-price-sub">
        Reserve ${detail.reservePrice} 💎 &middot;
        ${detail.status === 'active'
          ? `<span class="auction-countdown" data-ends-at="${endsMs}">${formatCountdown(endsMs)}</span> left`
          : 'Auction ended'}
      </div>
      <div class="meta">${leaderLine}</div>
    </div>
  `;

  if (detail.status === 'active' && !detail.perspective.isSeller) {
    // Minimum floor for the input mirrors the server rule (see
    // server/auctions/bidding.ts:minimumNextBid): if the requester is
    // already the current leader they're adjusting their own ceiling,
    // so the floor is just the visible price (no INCREMENT bump); a
    // fresh bidder must clear visible + INCREMENT.
    const isLeader = detail.perspective.isLeader;
    const minBid = isLeader
      ? detail.visiblePrice
      : (detail.leaderUserId
        ? detail.visiblePrice + BID_INCREMENT
        : detail.reservePrice);
    // For a leader adjusting their max, default the input to their
    // current max so submitting-as-is is a no-op and typing a new
    // number is a clear diff. For a challenger, default to the
    // minimum acceptable bid (encourages the cheapest legal move).
    const defaultBid = isLeader && detail.perspective.myActiveMaxBid !== null
      ? detail.perspective.myActiveMaxBid
      : minBid;
    const form = document.createElement('form');
    form.className = 'auction-bid-form';
    const myMaxLine = detail.perspective.myActiveMaxBid !== null
      ? `<div class="meta">Your current max: ${detail.perspective.myActiveMaxBid} 💎</div>`
      : '';
    const buttonLabel = isLeader ? 'Update max bid' : 'Place bid';
    const inputLabel = isLeader ? 'Your new max bid' : 'Your max bid';
    form.innerHTML = `
      <label>
        ${inputLabel}
        <input type="number" name="maxBid" min="${minBid}" step="1" value="${defaultBid}" required />
      </label>
      <button type="submit" class="menu-btn primary-blue">${buttonLabel}</button>
      <div class="meta">Min: ${minBid} 💎 &middot; you'll only pay the final visible price if you win.</div>
      ${myMaxLine}
    `;
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const input = form.elements.namedItem('maxBid') as HTMLInputElement;
      const value = parseInt(input.value, 10);
      if (!Number.isFinite(value)) return;
      void doBid(auctionId, value, onGemsChanged, refresh);
    });
    right.appendChild(form);
  } else if (detail.perspective.isSeller) {
    const note = document.createElement('div');
    note.className = 'meta';
    note.textContent = 'You are the seller — you cannot bid on your own listing.';
    right.appendChild(note);
  }

  const log = document.createElement('div');
  log.className = 'auction-history';
  log.innerHTML = `<h2 class="section-title">Activity</h2>`;
  const listEl = document.createElement('div');
  listEl.className = 'auction-history-list';
  if (detail.bids.length === 0) {
    listEl.innerHTML = '<div class="meta">No bids yet.</div>';
  } else {
    for (const b of detail.bids) {
      listEl.appendChild(bidRow(b));
    }
  }
  log.appendChild(listEl);
  right.appendChild(log);

  // Flavor quote + "About this card" lore panel. Positioned at the
  // bottom of the info column: the bidding action lives higher up
  // (the important stuff), and in landscape this fills the otherwise-
  // blank vertical space below the shorter info panel.
  //
  // Dedup: some old cards were generated before flavor + description
  // split into separate fields, so they carry the same string in
  // both. When identical, only the "About" box renders — the boxed
  // lore treatment is the more prominent presentation.
  const flavor = card?.flavor?.trim() ?? '';
  const description = card?.description?.trim() ?? '';
  const showFlavor = !!flavor && flavor !== description;
  const showAbout = !!description;
  if (showFlavor) {
    const quote = document.createElement('div');
    quote.className = 'auction-lore-flavor card-detail-flavor';
    quote.textContent = `"${flavor}"`;
    right.appendChild(quote);
  }
  // Tag chips between the flavor quote and the "About" box. Reuses
  // .card-detail-tags / .card-detail-tag from the deck-editor detail
  // modal so the pill visual is consistent across surfaces.
  if (card && card.tags.length > 0) {
    const tagsEl = document.createElement('div');
    tagsEl.className = 'card-detail-tags auction-lore-tags';
    tagsEl.innerHTML = card.tags
      .map(t => `<span class="card-detail-tag">${escapeHtml(t)}</span>`)
      .join('');
    right.appendChild(tagsEl);
  }
  if (showAbout) {
    const about = document.createElement('section');
    about.className = 'card-lore';
    about.innerHTML = `
      <div class="card-lore-heading">About this card</div>
      <p class="card-lore-body">${escapeHtml(description)}</p>
    `;
    right.appendChild(about);
  }

  layout.appendChild(right);
  shell.appendChild(layout);
}

function bidRow(b: BidHistoryEntry): HTMLElement {
  const row = document.createElement('div');
  row.className = 'auction-history-row';
  row.innerHTML = `
    <span class="auction-history-name">${escapeHtml(b.displayName)}</span>
    <span class="auction-history-price">${b.visiblePrice} 💎</span>
  `;
  return row;
}

function statusLineHtml(d: AuctionDetail): string {
  switch (d.status) {
    case 'active':        return '<span class="auction-badge active">Active</span>';
    case 'closed_sold':   return d.perspective.isWinner
      ? '<span class="auction-badge won">You won! Claim below.</span>'
      : '<span class="auction-badge closed">Sold</span>';
    case 'closed_unsold': return '<span class="auction-badge closed">Ended — reserve not met</span>';
    case 'claimed':       return '<span class="auction-badge claimed">Claimed</span>';
    case 'canceled':      return '<span class="auction-badge closed">Canceled</span>';
  }
}

async function doBid(
  auctionId: string,
  maxBid: number,
  onGemsChanged: (gems: number) => void,
  refresh: () => Promise<void>,
): Promise<void> {
  try {
    const resp = await apiClient.placeAuctionBid(auctionId, maxBid);
    onGemsChanged(resp.myGems);
    await refresh();
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    showNotice({ title: 'Bid rejected', message: msg });
  }
}

async function doClaim(
  auctionId: string,
  cardId: string,
  variant: number,
  onBack: () => void,
  onClaimed: (cardId: string, art: string) => void,
  onGemsChanged: (gems: number) => void,
): Promise<void> {
  try {
    const resp = await apiClient.claimAuction(auctionId, variant);
    // Bump the client's local collection AND update the catalog def's
    // art URL to the cache-busted version the server just wrote. Both
    // are needed: the collection bump makes the deck editor see the
    // new card; the art update makes it render the right variant
    // (otherwise the browser's 24h cache of the pre-claim `<id>.png`
    // wins and shows whichever bytes were there before).
    onClaimed(cardId, resp.art);
    // Winner overage was refunded at close time (during the sweep),
    // not now. But the client hasn't necessarily seen that refund yet
    // if the sweep ran on someone else's request. Echo the server's
    // current gems balance so the header reconciles.
    onGemsChanged(resp.gems);
    showNotice({
      title: 'Claimed!',
      message: 'The card has been added to your collection with your chosen art.',
      onDismissed: onBack,
    });
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    showNotice({ title: 'Claim failed', message: msg });
  }
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** Read the current gem balance out of the DOM. Main.ts patches
 *  `.player-gems` in place after every bid via
 *  `onGemsChanged`, so this is the freshest number without threading
 *  the balance back through the router. Returns null if the element
 *  is missing or the text can't be parsed — caller falls back to the
 *  last-known value. */
function readDomGems(): number | null {
  const el = document.querySelector('.player-gems');
  if (!el) return null;
  const m = el.textContent?.match(/-?\d+/);
  return m ? parseInt(m[0], 10) : null;
}
